import React, { useState, useEffect, useCallback } from 'react';
import { useVariant } from '../ABTesting/ABTestProvider';
import { useAuth } from '../../contexts/AuthContext';
import { API } from '../../utils/api';

// Admin emails that can hide Maestro Bistro ads
const ADMIN_EMAILS = [
  'jjspilot24@gmail.com',
  'johnselman34@gmail.com', 
  'john.1976.selman@gmail.com'
];

// Book promotional images - including new ebook cover
const BOOK_IMAGES = [
  "https://customer-assets.emergentagent.com/job_protocol-hub-5/artifacts/149zoktj_ebook.jpg",
  "https://customer-assets.emergentagent.com/job_search-explorer-5/artifacts/024v1r34_Letters%20to%20Evelyn%20advertisement%201.jpg",
  "https://customer-assets.emergentagent.com/job_search-explorer-5/artifacts/e90a1rlq_Letters%20to%20Evelyn%20advertisement%202.jpg",
  "https://customer-assets.emergentagent.com/job_search-explorer-5/artifacts/ccdegcr8_Letters%20to%20Evelyn%20advertisement%203.jpg",
  "https://customer-assets.emergentagent.com/job_search-explorer-5/artifacts/3gqu0i0v_Letters%20to%20Evelyn%20advertisement%204.jpg"
];

const FUNNY_TAGLINES = [
  { image: 0, text: "⚡ LIMITED TIME: $2.99 - Less Than Your Coffee! ☕", subtext: "19 Five-Star Reviews • Optioned for Film • Read It Before Hollywood Does!" },
  { image: 1, text: "WROTE A BOOK. UNIVERSE FACT-CHECKED IT. IT PASSED.", subtext: "World Record Holder • Navy Pilot • Now Bestselling Author!" },
  { image: 2, text: "THERAPIST: THIS IS A LOT TO UNPACK.", subtext: "Warning: May cause uncontrollable laughter and existential questioning!" },
  { image: 3, text: "I FLEW JETS. THEN REALITY BROKE.", subtext: "From cockpit to cosmic encounters - the story Hollywood couldn't ignore!" },
  { image: 4, text: "🚀 TERROR OF THE COSMIC GULPER 🚀", subtext: "A comedy that will haunt you (in the best way possible!)" }
];

// Urgency/Scarcity messaging for CTAs
const URGENCY_MESSAGES = [
  "🔥 Over 10,000 readers can't be wrong!",
  "⏰ Film production starting soon - read the original first!",
  "💫 The book that made Hollywood take notice!",
  "🎬 Before it hits theaters - experience the source!",
  "✨ Join thousands who already know the secret!",
];

// CTA Button Styles for A/B testing
const CTA_STYLES = {
  gradient_pink_orange: {
    background: 'linear-gradient(135deg, #FDE68A, #F59E0B)',
    boxShadow: '0 8px 30px rgba(253, 230, 138, 0.5)'
  },
  gradient_green: {
    background: 'linear-gradient(135deg, #10b981, #059669)',
    boxShadow: '0 8px 30px rgba(16, 185, 129, 0.5)'
  },
  gradient_purple: {
    background: 'linear-gradient(135deg, #FDE047, #FCD34D)',
    boxShadow: '0 8px 30px rgba(253, 224, 71, 0.5)'
  }
};

// Professional Reviews from Readers' Favorite
const PROFESSIONAL_REVIEWS = [
  { reviewer: "Divine Zape", quote: "...a profound and unforgettable literary piece.", stars: 5 },
  { reviewer: "Paul Zietsman", quote: "...exceedingly brilliant.", stars: 5 },
  { reviewer: "Ruffina Oserio", quote: "...spellbinding...", stars: 5 },
  { reviewer: "Lauren Jones", quote: "The author's imagination is off the charts.", stars: 5 }
];

// Top Pilot Enterprises - Parent Company Banner
const TopPilotBanner = () => (
  <div style={{
    background: 'linear-gradient(135deg, #1a0a30 0%, #2d1b4e 50%, #1a365d 100%)',
    padding: '12px 20px',
    textAlign: 'center',
    borderBottom: '2px solid rgba(251, 191, 36, 0.5)'
  }}>
    <div style={{
      color: '#047857',
      fontSize: '0.85rem',
      fontWeight: 700,
      letterSpacing: '2px',
      textTransform: 'uppercase'
    }}>
      ✈️ A <span style={{ color: '#0F172A' }}>Top Pilot Enterprises, Inc.</span> Venture ✈️
    </div>
    <div style={{
      color: '#1e293b',
      fontSize: '0.7rem',
      marginTop: 4,
      fontStyle: 'italic'
    }}>
      "Three ventures. One mission. Zero turbulence." (Okay, maybe a little during lunch rush at the bistro.)
    </div>
  </div>
);

// Maestro Bistro Promo Section
const MaestroBistroSection = () => (
  <div style={{
    background: 'linear-gradient(135deg, rgba(45, 20, 5, 0.7), rgba(70, 35, 15, 0.6))',
    padding: 20,
    borderRadius: 15,
    border: '2px solid rgba(139, 69, 19, 0.6)',
    marginTop: 15
  }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>
      {/* Beef Rouladen Dish - DARKER dark brown with rich gravy, bacon, dijon mustard visible */}
      <div style={{
        width: 100,
        height: 100,
        borderRadius: 12,
        background: 'linear-gradient(180deg, #1a0f08 0%, #120a05 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: 'inset 0 -10px 25px rgba(0,0,0,0.8), 0 6px 15px rgba(0,0,0,0.6)',
        position: 'relative',
        overflow: 'hidden',
        border: '3px solid #3d2a1f'
      }}>
        {/* DARK reddish-brown gravy pool - even richer */}
        <div style={{
          position: 'absolute',
          bottom: 8,
          width: '85%',
          height: '60%',
          background: 'linear-gradient(180deg, #4a2512 0%, #3a1c0d 40%, #2a1408 100%)',
          borderRadius: '50% 50% 45% 45%',
          boxShadow: 'inset 0 3px 10px rgba(80, 40, 20, 0.6)'
        }}/>
        {/* Beef Rouladen roll #1 - VERY dark brown with bacon visible */}
        <div style={{
          position: 'absolute',
          bottom: 22,
          left: '15%',
          width: 28,
          height: 16,
          background: 'linear-gradient(135deg, #2d1408 0%, #1f0c04 50%, #150a02 100%)',
          borderRadius: '45% 45% 40% 40%',
          boxShadow: '0 3px 6px rgba(0,0,0,0.7)',
          border: '1px solid #3d2010'
        }}>
          {/* Bacon stripe - darker */}
          <div style={{ position: 'absolute', top: 4, left: 3, width: 22, height: 2, background: '#5c2d18', borderRadius: 1, opacity: 0.8 }}/>
          {/* Dijon mustard hint - subtle yellow */}
          <div style={{ position: 'absolute', top: 7, left: 8, width: 12, height: 2, background: '#9a8020', borderRadius: 1, opacity: 0.6 }}/>
        </div>
        {/* Beef Rouladen roll #2 - DARKER */}
        <div style={{
          position: 'absolute',
          bottom: 20,
          left: '45%',
          width: 24,
          height: 14,
          background: 'linear-gradient(135deg, #2d1408 0%, #1a0c04 100%)',
          borderRadius: '45%',
          boxShadow: '0 2px 4px rgba(0,0,0,0.6)',
          border: '1px solid #3d2010'
        }}/>
        {/* Egg noodles covered in DARK brown gravy */}
        <div style={{
          position: 'absolute',
          bottom: 14,
          right: '12%',
          width: 28,
          height: 22,
          background: 'linear-gradient(135deg, #8a7030 0%, #6a5520 50%, #4a3a15 100%)',
          borderRadius: '35%',
          boxShadow: 'inset 0 2px 4px rgba(60, 40, 15, 0.5)'
        }}>
          {/* Gravy drizzle on noodles - darker */}
          <div style={{ position: 'absolute', top: 3, left: 5, width: 18, height: 3, background: '#3a1c0d', borderRadius: 2, opacity: 0.7 }}/>
          <div style={{ position: 'absolute', top: 8, left: 8, width: 12, height: 2, background: '#2a1408', borderRadius: 1, opacity: 0.6 }}/>
        </div>
        {/* Peas scattered - darker green */}
        <div style={{ position: 'absolute', bottom: 28, left: '12%', width: 6, height: 6, background: 'linear-gradient(135deg, #2d5a2d, #1f4a1f)', borderRadius: '50%', boxShadow: 'inset 0 -1px 2px rgba(0,0,0,0.4)' }}/>
        <div style={{ position: 'absolute', bottom: 34, left: '18%', width: 5, height: 5, background: 'linear-gradient(135deg, #3a6a3a, #2d5a2d)', borderRadius: '50%' }}/>
        <div style={{ position: 'absolute', bottom: 26, left: '26%', width: 5, height: 5, background: '#1f4a1f', borderRadius: '50%' }}/>
        <div style={{ position: 'absolute', bottom: 32, right: '35%', width: 5, height: 5, background: '#2d5a2d', borderRadius: '50%' }}/>
        {/* Carrot slices - slightly darker orange */}
        <div style={{ position: 'absolute', bottom: 30, right: '15%', width: 9, height: 6, background: 'linear-gradient(135deg, #c05010, #a04008)', borderRadius: '40%', boxShadow: 'inset 0 1px 2px rgba(200,100,30,0.3)' }}/>
        <div style={{ position: 'absolute', bottom: 24, right: '25%', width: 7, height: 5, background: 'linear-gradient(135deg, #a04008, #803000)', borderRadius: '40%' }}/>
      </div>
      
      {/* Fish Chowder Bowl - richer with potatoes, bacon, onions, white fish visible */}
      <div style={{
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: 'linear-gradient(180deg, #a89070 0%, #988060 50%, #887050 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: 'inset 0 -8px 20px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.4)',
        position: 'relative',
        overflow: 'hidden',
        border: '3px solid #786850'
      }}>
        {/* Creamy chowder base - slightly richer cream color */}
        <div style={{
          position: 'absolute',
          bottom: 8,
          width: '80%',
          height: '55%',
          background: 'linear-gradient(180deg, #e8e0c8 0%, #d8d0b0 50%, #c8c098 100%)',
          borderRadius: '50% 50% 45% 45%',
          boxShadow: 'inset 0 2px 6px rgba(200, 180, 140, 0.4)'
        }}/>
        {/* White fish chunks */}
        <div style={{ position: 'absolute', bottom: 18, left: '20%', width: 14, height: 10, background: 'linear-gradient(135deg, #f8f8f0, #e8e8e0)', borderRadius: '30%', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}/>
        <div style={{ position: 'absolute', bottom: 22, right: '22%', width: 12, height: 8, background: 'linear-gradient(135deg, #f0f0e8, #e0e0d8)', borderRadius: '35%' }}/>
        {/* Potato chunks */}
        <div style={{ position: 'absolute', bottom: 16, left: '40%', width: 10, height: 8, background: 'linear-gradient(135deg, #f5e8c0, #e8d8a8)', borderRadius: '25%' }}/>
        <div style={{ position: 'absolute', bottom: 24, right: '35%', width: 8, height: 7, background: '#f0e0b0', borderRadius: '30%' }}/>
        {/* Bacon bits */}
        <div style={{ position: 'absolute', bottom: 28, left: '28%', width: 6, height: 3, background: '#8b4020', borderRadius: 1 }}/>
        <div style={{ position: 'absolute', bottom: 20, right: '30%', width: 5, height: 2, background: '#9a4828', borderRadius: 1 }}/>
        {/* Yellow onion pieces */}
        <div style={{ position: 'absolute', bottom: 14, left: '32%', width: 5, height: 4, background: '#e8d090', borderRadius: '40%', opacity: 0.8 }}/>
        <div style={{ position: 'absolute', bottom: 26, left: '50%', width: 4, height: 3, background: '#f0d898', borderRadius: '30%', opacity: 0.7 }}/>
      </div>
      
      <div style={{ flex: 1, minWidth: 220 }}>
        <h3 style={{
          fontSize: '1.4rem',
          fontWeight: 800,
          color: '#d97706',
          marginBottom: 5,
          textShadow: '0 1px 2px rgba(0,0,0,0.5)'
        }}>
          MAESTRO BISTRO
        </h3>
        <div style={{ color: '#047857', fontSize: '0.9rem', marginBottom: 10 }}>
          🏪 On the Mall • Brunswick, Maine
        </div>
        <div style={{ color: '#0F172A', fontSize: '0.85rem', lineHeight: 1.7 }}>
          <div style={{ marginBottom: 8 }}>
            <strong style={{ color: '#d97706' }}>🥩 deLectaBLe Beef Rouladen</strong>
            <div style={{ color: '#d4c4a0', fontSize: '0.8rem', marginLeft: 22 }}>
              Dark brown beef rolls with bacon & dijon mustard inside, smothered in rich reddish-brown gravy, served with egg noodles, sweet peas & carrots
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <strong style={{ color: '#10b981' }}>🥬 Vegetable Rouladen</strong>
            <div style={{ color: '#d4c4a0', fontSize: '0.8rem', marginLeft: 22 }}>
              Fresh garden vegetables in savory brown gravy - It's German Cuisine!
            </div>
          </div>
          <div>
            <strong style={{ color: '#38bdf8' }}>🐟 Maine Fish Chowder</strong>
            <div style={{ color: '#d4c4a0', fontSize: '0.8rem', marginLeft: 22 }}>
              Creamy chowder with tender white fish, potatoes, crispy bacon bits & yellow onions
            </div>
          </div>
        </div>
        <div style={{
          marginTop: 12,
          padding: '8px 15px',
          background: 'rgba(139, 69, 19, 0.3)',
          borderRadius: 10,
          color: '#047857',
          fontSize: '0.8rem',
          fontStyle: 'italic',
          fontWeight: 600,
          border: '1px solid rgba(210, 105, 30, 0.4)'
        }}>
          "Appropriate & conscientable prices for appropriately & conscientiously AMAZING food!" 🎻
        </div>
        {/* NEW: Real food photography images */}
        <div style={{ display: 'flex', gap: 10, marginTop: 15, flexWrap: 'wrap', justifyContent: 'center' }}>
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b9901627-c41f-49f8-a5ec-8ae9029551bf/images/8053964189363913cfd0b52cd33694358d4e8119053ba844d1e6ff0c9914f362.png" 
            alt="Beef Rouladen" 
            style={{ width: 100, height: 100, borderRadius: 10, objectFit: 'cover', border: '2px solid #5c3d2e' }}
          />
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b9901627-c41f-49f8-a5ec-8ae9029551bf/images/4def025127b6d6aac68bef8c5e1794d7666cc08d333de695d1136af7c65c430c.png" 
            alt="Vegetable Rouladen" 
            style={{ width: 100, height: 100, borderRadius: 10, objectFit: 'cover', border: '2px solid #10b981' }}
          />
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b9901627-c41f-49f8-a5ec-8ae9029551bf/images/21731046688629d8674f306fdedabb0c12b0d652fd365d2c4031948b7fd640d9.png" 
            alt="Fish Chowder with Red Potatoes" 
            style={{ width: 100, height: 100, borderRadius: 10, objectFit: 'cover', border: '2px solid #38bdf8' }}
          />
        </div>
      </div>
    </div>
  </div>
);

// InfoPilot Promo Section
const InfoPilotSection = () => (
  <div style={{
    background: 'linear-gradient(135deg, rgba(252, 211, 77, 0.2), rgba(245, 158, 11, 0.2))',
    padding: 20,
    borderRadius: 15,
    border: '2px solid rgba(252, 211, 77, 0.5)',
    marginTop: 15
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 15, flexWrap: 'wrap' }}>
      <div style={{
        fontSize: '2.5rem',
        background: 'linear-gradient(135deg, #047857, #0F172A)',
        borderRadius: 15,
        width: 70,
        height: 70,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 900,
        color: '#0F172A'
      }}>
        🔍
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <h3 style={{
          fontSize: '1.4rem',
          fontWeight: 800,
          background: 'linear-gradient(135deg, #047857, #0F172A)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: 5
        }}>
          INFOPILOT EXPLORER
        </h3>
        <div style={{ color: '#047857', fontSize: '0.9rem', marginBottom: 8 }}>
          🌐 World Wide Information Exchange
        </div>
        <div style={{ color: '#0F172A', fontSize: '0.85rem', lineHeight: 1.6 }}>
          <div style={{ marginBottom: 5 }}>
            ⚡ <strong style={{ color: '#FCD34D' }}>InfoJet 2.0™</strong> - Proprietary search & categorization language
          </div>
          <div style={{ marginBottom: 5 }}>
            🗺️ <strong style={{ color: '#3b82f6' }}>Interactive Maps</strong> - Geolocated results worldwide
          </div>
          <div>
            🏪 <strong style={{ color: '#10b981' }}>Protocol Marketplace</strong> - Buy & sell search protocols
          </div>
        </div>
        <div style={{
          marginTop: 12,
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap'
        }}>
          <span style={{
            padding: '6px 12px',
            background: 'rgba(16, 185, 129, 0.2)',
            color: '#10b981',
            borderRadius: 20,
            fontSize: '0.75rem',
            fontWeight: 700
          }}>
            ✓ 100% FREE
          </span>
          <span style={{
            padding: '6px 12px',
            background: 'rgba(252, 211, 77, 0.2)',
            color: '#047857',
            borderRadius: 20,
            fontSize: '0.75rem',
            fontWeight: 700
          }}>
            ✓ Google Sign-In
          </span>
          <span style={{
            padding: '6px 12px',
            background: 'rgba(251, 191, 36, 0.2)',
            color: '#047857',
            borderRadius: 20,
            fontSize: '0.75rem',
            fontWeight: 700
          }}>
            ✓ Copyright Protected
          </span>
        </div>
      </div>
    </div>
    
    {/* IT'S A BEAR - Marketplace Power Section */}
    <div style={{
      marginTop: 20,
      padding: 20,
      background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.3), rgba(101, 67, 33, 0.4))',
      borderRadius: 15,
      border: '3px solid rgba(139, 69, 19, 0.6)',
      position: 'relative',
      overflow: 'hidden'
    }} data-testid="its-a-bear-section">
      {/* Bear paw prints background */}
      <div style={{
        position: 'absolute',
        top: 10,
        right: 10,
        opacity: 0.15,
        fontSize: '4rem',
        transform: 'rotate(15deg)'
      }}>
        🐻
      </div>
      
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 15,
          marginBottom: 15
        }}>
          <span style={{ fontSize: '3rem' }}>🐻</span>
          <div>
            <div style={{
              fontSize: '0.85rem',
              color: '#d4a574',
              fontWeight: 600,
              letterSpacing: '2px',
              textTransform: 'uppercase',
              marginBottom: 5
            }}>
              THE INFOPILOT MARKETPLACE
            </div>
            <div style={{
              fontSize: '2rem',
              fontWeight: 900,
              background: 'linear-gradient(135deg, #047857, #0F172A)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '3px'
            }}>
              IT'S A BEAR.
            </div>
          </div>
        </div>
        
        <p style={{
          color: '#e8d5b7',
          fontSize: '0.95rem',
          lineHeight: 1.7,
          marginBottom: 15
        }}>
          <strong style={{ color: '#daa520' }}>The most POWERFUL search protocol marketplace in existence.</strong>{' '}
          Like a Kodiak bear in the app market, InfoPilot Explorer is an <em>apex predator</em>—
          massive in capability, fierce in functionality, and absolutely unstoppable. 
          Other apps? <span style={{ color: '#f87171' }}>Salmon swimming upstream.</span>
        </p>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12,
          marginBottom: 15
        }}>
          <div style={{ textAlign: 'center', padding: 10, background: 'rgba(0,0,0,0.2)', borderRadius: 10 }}>
            <div style={{ color: '#daa520', fontSize: '1.5rem', fontWeight: 900 }}>∞</div>
            <div style={{ color: '#d4a574', fontSize: '0.75rem' }}>Unlimited Protocols</div>
          </div>
          <div style={{ textAlign: 'center', padding: 10, background: 'rgba(0,0,0,0.2)', borderRadius: 10 }}>
            <div style={{ color: '#daa520', fontSize: '1.5rem', fontWeight: 900 }}>🌍</div>
            <div style={{ color: '#d4a574', fontSize: '0.75rem' }}>Global Reach</div>
          </div>
          <div style={{ textAlign: 'center', padding: 10, background: 'rgba(0,0,0,0.2)', borderRadius: 10 }}>
            <div style={{ color: '#daa520', fontSize: '1.5rem', fontWeight: 900 }}>AI</div>
            <div style={{ color: '#d4a574', fontSize: '0.75rem' }}>Powered Search</div>
          </div>
          <div style={{ textAlign: 'center', padding: 10, background: 'rgba(0,0,0,0.2)', borderRadius: 10 }}>
            <div style={{ color: '#daa520', fontSize: '1.5rem', fontWeight: 900 }}>💰</div>
            <div style={{ color: '#d4a574', fontSize: '0.75rem' }}>Earn Revenue</div>
          </div>
        </div>
        
        <div style={{
          background: 'rgba(218, 165, 32, 0.2)',
          borderRadius: 10,
          padding: 12,
          border: '1px solid rgba(218, 165, 32, 0.4)'
        }}>
          <p style={{
            color: '#047857',
            fontSize: '0.85rem',
            margin: 0,
            fontStyle: 'italic',
            textAlign: 'center'
          }}>
            "Other apps nibble at the edges. InfoPilot? It devours the competition whole."
            <br />
            <span style={{ fontSize: '0.75rem', color: '#d4a574' }}>— A very wise bear 🐻</span>
          </p>
        </div>
      </div>
    </div>
  </div>
);

const BookPromoBanner = () => {
  const { user, token } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [hideMaestroBistro, setHideMaestroBistro] = useState(false);
  const [compactMode, setCompactMode] = useState(true); // Default to compact mode for smaller ads
  
  // Check if user is an admin who can hide ads
  const canHideAds = user?.email && ADMIN_EMAILS.map(e => e.toLowerCase()).includes(user.email.toLowerCase());
  
  // Load hide preference from localStorage or user settings
  useEffect(() => {
    if (canHideAds) {
      const savedPref = localStorage.getItem('hideMaestroBistro');
      if (savedPref !== null) {
        setHideMaestroBistro(savedPref === 'true');
      }
      const savedCompact = localStorage.getItem('compactAds');
      if (savedCompact !== null) {
        setCompactMode(savedCompact === 'true');
      }
    }
  }, [canHideAds, user?.email]);
  
  // Toggle Maestro Bistro visibility
  const toggleMaestroBistro = () => {
    const newValue = !hideMaestroBistro;
    setHideMaestroBistro(newValue);
    localStorage.setItem('hideMaestroBistro', String(newValue));
  };
  
  // Toggle compact mode
  const toggleCompactMode = () => {
    const newValue = !compactMode;
    setCompactMode(newValue);
    localStorage.setItem('compactAds', String(newValue));
  };
  
  // A/B Testing hooks for headline and CTA
  const { variant: headlineVariant, trackEvent: trackHeadlineEvent } = useVariant('book_promo_headline');
  const { variant: ctaVariant, trackEvent: trackCtaEvent } = useVariant('book_promo_cta_button');
  
  // Track impression on mount
  useEffect(() => {
    if (headlineVariant) {
      trackHeadlineEvent('impression');
    }
    if (ctaVariant) {
      trackCtaEvent('impression');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headlineVariant?.variant_id, ctaVariant?.variant_id]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % BOOK_IMAGES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  // Use A/B variant content or fallback to defaults
  const currentTagline = headlineVariant?.content 
    ? { text: headlineVariant.content.headline, subtext: headlineVariant.content.subtext }
    : FUNNY_TAGLINES[currentImageIndex];
  
  const ctaContent = ctaVariant?.content || { text: '🛒 GET IT NOW - Only $2.99!', style: 'gradient_pink_orange' };
  const ctaStyle = CTA_STYLES[ctaContent.style] || CTA_STYLES.gradient_pink_orange;
  
  // Handle CTA click with A/B tracking
  const handleCtaClick = () => {
    if (ctaVariant) {
      trackCtaEvent('click');
    }
    // Conversion is tracked when user actually purchases (external)
  };

  // Compact mode renders a much smaller banner
  if (compactMode) {
    return (
      <div data-testid="book-promo-banner-compact" style={{
        background: 'linear-gradient(135deg, rgba(252, 211, 77, 0.2), rgba(253, 230, 138, 0.15))',
        borderRadius: 12,
        padding: '10px 15px',
        marginBottom: 15,
        border: '1px solid rgba(253, 230, 138, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
          <span style={{ fontSize: '1.5rem' }}>📚</span>
          <div>
            <div style={{ color: '#047857', fontWeight: 700, fontSize: '0.9rem' }}>
              Letters to Evelyn - $2.99
            </div>
            <div style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>
              19 Five-Star Reviews • Optioned for Film
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <a
            href="https://amzn.to/4iOMnYi"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '6px 14px',
              background: 'linear-gradient(135deg, #FDE68A, #F59E0B)',
              color: '#0F172A',
              borderRadius: 15,
              textDecoration: 'none',
              fontSize: '0.8rem',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            Get Book
          </a>
          <button
            onClick={toggleCompactMode}
            style={{
              background: 'rgba(252, 211, 77, 0.2)',
              border: '1px solid rgba(252, 211, 77, 0.4)',
              color: '#047857',
              padding: '4px 8px',
              borderRadius: 6,
              fontSize: '0.7rem',
              cursor: 'pointer'
            }}
          >
            Expand
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="book-promo-banner" style={{
      background: 'linear-gradient(135deg, rgba(20, 10, 40, 0.98), rgba(80, 20, 100, 0.95))',
      borderRadius: 20,
      padding: 0,
      marginBottom: 25,
      border: '3px solid rgba(253, 230, 138, 0.7)',
      overflow: 'hidden',
      boxShadow: '0 25px 80px rgba(252, 211, 77, 0.5)',
      position: 'relative'
    }}>
      {/* Compact mode button */}
      <button
        onClick={toggleCompactMode}
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          background: 'rgba(0,0,0,0.5)',
          border: '1px solid rgba(255,255,255,0.2)',
          color: '#a1a1aa',
          padding: '4px 10px',
          borderRadius: 15,
          fontSize: '0.7rem',
          cursor: 'pointer',
          zIndex: 10
        }}
        title="Minimize advertisement"
      >
        ─ Hide
      </button>
      
      {/* Top Pilot Enterprises Banner */}
      <TopPilotBanner />
      
      {/* Animated Header Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #FCD34D, #FDE68A, #F59E0B)',
        padding: '18px 25px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Shimmer effect */}
        <div style={{
          position: 'absolute',
          top: 0, left: '-100%', right: 0, bottom: 0,
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
          animation: 'shimmer 3s infinite'
        }} />
        
        {/* A/B Test Indicator (only in dev) */}
        {process.env.NODE_ENV === 'development' && headlineVariant && (
          <div style={{
            position: 'absolute',
            top: 2,
            left: 5,
            background: 'rgba(0,0,0,0.5)',
            padding: '2px 6px',
            borderRadius: 4,
            fontSize: '0.6rem',
            color: '#10b981'
          }}>
            A/B: {headlineVariant.variant_id}
          </div>
        )}
        
        <div style={{
          position: 'absolute',
          top: 8,
          right: 15,
          background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
          color: '#000',
          padding: '6px 18px',
          borderRadius: 20,
          fontWeight: 800,
          fontSize: '0.8rem',
          boxShadow: '0 4px 20px rgba(251, 191, 36, 0.6)',
          animation: 'pulse 2s infinite'
        }}>
          🎬 OPTIONED FOR FILM!
        </div>
        
        <div style={{
          fontSize: '1.6rem',
          fontWeight: 900,
          color: '#0F172A',
          textShadow: '2px 2px 8px rgba(0,0,0,0.4)',
          letterSpacing: '1px',
          transition: 'all 0.5s'
        }}>
          {currentTagline.text}
        </div>
        <div style={{
          fontSize: '1rem',
          color: 'rgba(255,255,255,0.95)',
          marginTop: 5,
          fontWeight: 600,
          fontStyle: 'italic'
        }}>
          {currentTagline.subtext}
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        display: 'flex',
        gap: 30,
        padding: 25,
        flexWrap: 'wrap',
        alignItems: 'flex-start'
      }}>
        {/* Book Image with Gallery */}
        <div style={{ flex: '0 0 auto', position: 'relative' }}>
          <div style={{
            width: 250,
            height: 320,
            borderRadius: 15,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(253, 230, 138, 0.6)',
            border: '4px solid rgba(255, 255, 255, 0.25)',
            transition: 'transform 0.3s',
            cursor: 'pointer'
          }}>
            <img 
              src={BOOK_IMAGES[currentImageIndex]} 
              alt="Letters to Evelyn - Funny Promo"
              style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'all 0.5s' }}
            />
          </div>
          {/* Thumbnail Gallery */}
          <div style={{ 
            display: 'flex', 
            gap: 10, 
            marginTop: 15,
            justifyContent: 'center'
          }}>
            {BOOK_IMAGES.map((img, idx) => (
              <div 
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                style={{
                  width: 50,
                  height: 50,
                  borderRadius: 10,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: idx === currentImageIndex ? '3px solid #FDE68A' : '2px solid rgba(255,255,255,0.3)',
                  opacity: idx === currentImageIndex ? 1 : 0.7,
                  transition: 'all 0.3s',
                  boxShadow: idx === currentImageIndex ? '0 0 15px rgba(253, 230, 138, 0.5)' : 'none'
                }}
              >
                <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ))}
          </div>
        </div>

        {/* Book Details */}
        <div style={{ flex: 1, minWidth: 300 }}>
          <h2 style={{
            fontSize: '2.2rem',
            fontWeight: 900,
            background: 'linear-gradient(135deg, #047857 0%, #0F172A 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 10,
            letterSpacing: '1px'
          }}>
            LETTERS TO EVELYN
          </h2>
          
          <div style={{ 
            color: '#047857', 
            fontSize: '1.1rem', 
            marginBottom: 8,
            fontWeight: 600
          }}>
            A Supernatural Thriller Comedy Memoir
          </div>
          
          <div style={{ 
            color: '#047857', 
            fontSize: '1rem', 
            marginBottom: 12,
            fontWeight: 700
          }}>
            By World Record Aviation Holder <span style={{ color: '#0F172A' }}>John Selman</span>
          </div>

          {/* Star Rating */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 15 }}>
            <span style={{ color: '#047857', fontSize: '1.4rem' }}>★★★★★</span>
            <span style={{ color: '#047857', fontWeight: 800, fontSize: '1rem' }}>19 Five-Star Reviews</span>
            <span style={{ 
              background: 'rgba(16, 185, 129, 0.2)', 
              color: '#10b981', 
              padding: '4px 12px', 
              borderRadius: 15,
              fontSize: '0.8rem',
              fontWeight: 700
            }}>
              Readers' Favorite
            </span>
          </div>

          {/* Key Selling Points */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ color: '#047857', fontSize: '0.95rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              ✈️ <span>Written by a Navy pilot who flew 10 aircraft types</span>
            </div>
            <div style={{ color: '#047857', fontSize: '0.95rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              👽 <span>Extraterrestrial encounters & cosmic visions</span>
            </div>
            <div style={{ color: '#047857', fontSize: '0.95rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              😂 <span>"Comedy that creeps into your mind and causes abrupt laughter"</span>
            </div>
          </div>

          {/* Review Quote */}
          <div style={{
            background: 'rgba(16, 185, 129, 0.15)',
            padding: 15,
            borderRadius: 12,
            marginBottom: 20,
            borderLeft: '4px solid #10b981'
          }}>
            <div style={{ color: '#10b981', fontStyle: 'italic', fontSize: '0.95rem', lineHeight: 1.5 }}>
              "The comical side is exceedingly brilliant... imagination off the charts. A true story that defies belief!"
            </div>
            <div style={{ 
              color: '#34d399', 
              fontSize: '0.85rem', 
              marginTop: 8, 
              fontWeight: 600
            }}>
              — Professional Review, Readers' Favorite ★★★★★
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 15 }}>
            <a 
              href="https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-buy-google-play"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                ...ctaStyle,
                color: '#0F172A',
                padding: '14px 28px',
                borderRadius: 30,
                fontWeight: 800,
                fontSize: '1rem',
                textDecoration: 'none',
                border: '2px solid rgba(255,255,255,0.2)',
                transition: 'transform 0.2s',
                animation: 'pulse 2s infinite'
              }}
              onClick={handleCtaClick}
            >
              {ctaContent.text}
            </a>
            <a 
              href="https://letters-to-evelyn.sintra.site"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: 'rgba(252, 211, 77, 0.3)',
                color: '#047857',
                padding: '14px 24px',
                borderRadius: 30,
                fontWeight: 700,
                fontSize: '1rem',
                textDecoration: 'none',
                border: '2px solid rgba(252, 211, 77, 0.5)'
              }}
            >
              🌐 OFFICIAL SITE
            </a>
            <a 
              href="https://readersfavorite.com/book-review/letters-to-evelyn"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: 'rgba(16, 185, 129, 0.2)',
                color: '#10b981',
                padding: '14px 24px',
                borderRadius: 30,
                fontWeight: 700,
                fontSize: '1rem',
                textDecoration: 'none',
                border: '2px solid rgba(16, 185, 129, 0.5)'
              }}
            >
              ⭐ SEE 19 Five-Star Reviews
            </a>
          </div>

          {/* Urgency Message - Rotating */}
          <div style={{
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 12,
            padding: '10px 16px',
            marginBottom: 15,
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}>
            <span style={{ fontSize: '1.2rem', animation: 'pulse 1.5s infinite' }}>🔥</span>
            <span style={{ color: '#f87171', fontSize: '0.9rem', fontWeight: 600 }}>
              {URGENCY_MESSAGES[Math.floor(Date.now() / 10000) % URGENCY_MESSAGES.length]}
            </span>
          </div>

          {/* Film Badge */}
          <div style={{
            display: 'inline-block',
            background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.3))',
            padding: '12px 20px',
            borderRadius: 25,
            border: '2px solid rgba(251, 191, 36, 0.6)'
          }}>
            <span style={{ color: '#047857', fontWeight: 800, fontSize: '0.9rem' }}>
              🎬 Hollywood couldn't resist - OPTIONED FOR FILM!
            </span>
          </div>
        </div>
      </div>

      {/* Other Top Pilot Enterprises Ventures */}
      <div style={{ padding: '0 25px 25px 25px' }}>
        <div style={{
          textAlign: 'center',
          color: '#a1a1aa',
          fontSize: '0.8rem',
          marginBottom: 15,
          fontWeight: 600,
          letterSpacing: '1px',
          textTransform: 'uppercase'
        }}>
          — More from Top Pilot Enterprises, Inc. —
        </div>
        
        {/* InfoPilot Promo */}
        <InfoPilotSection />
        
        {/* Admin Control for Maestro Bistro */}
        {canHideAds && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 15
          }}>
            <button
              onClick={toggleMaestroBistro}
              style={{
                padding: '8px 16px',
                background: hideMaestroBistro ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                border: `1px solid ${hideMaestroBistro ? 'rgba(16, 185, 129, 0.5)' : 'rgba(239, 68, 68, 0.5)'}`,
                borderRadius: 20,
                color: hideMaestroBistro ? '#10b981' : '#f87171',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 600
              }}
              data-testid="toggle-maestro-bistro"
            >
              {hideMaestroBistro ? '👁️ Show Maestro Bistro Ads' : '🙈 Hide Maestro Bistro Ads'}
            </button>
          </div>
        )}
        
        {/* Maestro Bistro Promo - Hidden if admin toggled off */}
        {!hideMaestroBistro && <MaestroBistroSection />}
        
        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: 20,
          padding: 15,
          background: 'rgba(0,0,0,0.3)',
          borderRadius: 12
        }}>
          <div style={{ color: '#047857', fontSize: '0.85rem', fontWeight: 700, marginBottom: 5 }}>
            ✈️ Top Pilot Enterprises, Inc.
          </div>
          <div style={{ color: '#a1a1aa', fontSize: '0.7rem', fontStyle: 'italic' }}>
            "Where every venture reaches cruising altitude" • Cleared for takeoff since Day One
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookPromoBanner;
